import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Airplane, CheckCircle, XCircle, Clock, Trash, Plus, Calendar, Bed, HourglassLow, HandCoins, Warning } from "@phosphor-icons/react";

const TYPE_META = {
  paid: { label: "Congé payé", icon: Airplane, color: "#10B981" },
  rtt: { label: "RTT", icon: HourglassLow, color: "#3B82F6" },
  sick: { label: "Maladie", icon: Bed, color: "#F59E0B" },
  unpaid: { label: "Sans solde", icon: HandCoins, color: "#A78BFA" },
};

const STATUS_META = {
  pending: { label: "En attente", color: "#F59E0B" },
  approved: { label: "Acceptée", color: "#10B981" },
  rejected: { label: "Refusée", color: "#EF4444" },
};

export default function LeaveRequests() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "associate";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [decidingReq, setDecidingReq] = useState(null);
  const [myBalance, setMyBalance] = useState(null);
  const [allBalances, setAllBalances] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [reqR, meR, allR] = await Promise.all([
        api.get("/leave-requests"),
        api.get("/leave-balance/me"),
        isAdmin ? api.get("/leave-balance") : Promise.resolve({ data: [] }),
      ]);
      setItems(reqR.data || []);
      setMyBalance(meR.data);
      setAllBalances(allR.data || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const g = { pending: [], approved: [], rejected: [] };
    items.forEach((it) => { (g[it.status] || (g[it.status] = [])).push(it); });
    return g;
  }, [items]);

  const teamBalances = useMemo(
    () => allBalances.filter((b) => b.user_id !== user?.id),
    [allBalances, user?.id]
  );

  const cancel = async (it) => {
    if (!window.confirm("Annuler cette demande ?")) return;
    try { await api.delete(`/leave-requests/${it.id}`); toast.success("Demande annulée"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-4 space-y-4" data-testid="leave-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-bold text-2xl">Congés & Absences</h1>
          <div className="text-xs text-white/50">{isAdmin ? "Validation des demandes d'équipe" : "Vos demandes personnelles"}</div>
        </div>
        <Button onClick={() => setOpenCreate(true)} className="bg-[#005CFF] hover:bg-[#0047CC]" data-testid="leave-new-btn">
          <Plus size={13} weight="bold" className="mr-1" /> Nouvelle
        </Button>
      </div>

      {/* My balance (all users) */}
      {myBalance && (
        <div className="rounded-2xl bg-gradient-to-br from-emerald-500/10 to-[#005CFF]/5 border border-emerald-500/20 p-4" data-testid="my-balance-card">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1.5"><Airplane size={11} weight="duotone" /> Mon solde congés payés {myBalance.year}</div>
            {myBalance.over_budget && <span className="text-[9px] text-red-400 flex items-center gap-0.5"><Warning size={10} weight="fill" /> Dépassement</span>}
          </div>
          <div className="flex items-end gap-3">
            <div>
              <div className={`font-display font-bold text-4xl leading-none tabular-nums ${myBalance.over_budget ? "text-red-400" : myBalance.remaining <= 3 ? "text-amber-400" : "text-emerald-400"}`} data-testid="my-balance-remaining">
                {myBalance.remaining}
              </div>
              <div className="text-[10px] text-white/40 uppercase tracking-widest">jours restants</div>
            </div>
            <div className="flex-1 space-y-1.5 pb-1">
              <MiniBar label="Utilisés" value={myBalance.used} total={myBalance.allowance} color="#F59E0B" />
              <MiniBar label="En attente" value={myBalance.pending} total={myBalance.allowance} color="#7CB2FF" />
              <div className="text-[9px] text-white/40 font-mono">Sur {myBalance.allowance}j / an</div>
            </div>
          </div>
        </div>
      )}

      {/* Admin overview of everyone's balances */}
      {isAdmin && allBalances.length > 0 && (
        <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-3" data-testid="all-balances-card">
          <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2">Soldes de l&apos;équipe</div>
          <div className="space-y-1.5">
            {teamBalances.map((b) => (
              <div key={b.user_id} className="flex items-center gap-2 text-xs" data-testid={`team-balance-${b.user_id}`}>
                <div className="flex-1 truncate">
                  {b.user_name} {b.over_budget && <span className="text-red-400 text-[10px]">⚠ dépassement</span>}
                </div>
                <div className={`font-mono tabular-nums text-sm ${b.over_budget ? "text-red-400" : b.remaining <= 3 ? "text-amber-400" : "text-emerald-400"}`}>
                  {b.remaining}/{b.allowance}j
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending (highlighted for admins) */}
      {grouped.pending.length > 0 && (
        <Section title={`En attente (${grouped.pending.length})`} accent="#F59E0B">
          {grouped.pending.map((it) => (
            <LeaveRow key={it.id} item={it} isAdmin={isAdmin} onDecide={() => setDecidingReq(it)} onCancel={() => cancel(it)} user={user} />
          ))}
        </Section>
      )}

      {/* Processed */}
      {(grouped.approved.length > 0 || grouped.rejected.length > 0) && (
        <Section title="Historique" accent="#3B82F6">
          {[...grouped.approved, ...grouped.rejected].map((it) => (
            <LeaveRow key={it.id} item={it} isAdmin={isAdmin} onDecide={() => setDecidingReq(it)} onCancel={() => cancel(it)} user={user} />
          ))}
        </Section>
      )}

      {!loading && items.length === 0 && (
        <div className="rounded-2xl bg-white/[0.02] border border-white/5 p-8 text-center text-white/40 text-sm">
          Aucune demande pour le moment.
        </div>
      )}

      <CreateLeaveDialog open={openCreate} onOpenChange={setOpenCreate} onCreated={load} />
      <DecideDialog req={decidingReq} onOpenChange={(o) => !o && setDecidingReq(null)} onDone={load} />
    </div>
  );
}

function Section({ title, accent, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: accent }}>{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function MiniBar({ label, value, total, color }) {
  const pct = total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-[9px] mb-0.5">
        <span className="text-white/50">{label}</span>
        <span className="font-mono text-white/70">{value}j</span>
      </div>
      <div className="h-1 rounded-full bg-white/10 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

function LeaveRow({ item, isAdmin, onDecide, onCancel, user }) {
  const t = TYPE_META[item.type] || TYPE_META.paid;
  const s = STATUS_META[item.status] || STATUS_META.pending;
  const Icon = t.icon;
  const isOwn = item.user_id === user?.id;
  const canCancel = (isOwn && item.status === "pending") || isAdmin;

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-3" data-testid={`leave-row-${item.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: t.color + "22", color: t.color }}>
          <Icon size={16} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold">{t.label}</span>
            <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full" style={{ background: s.color + "22", color: s.color, border: `1px solid ${s.color}55` }}>{s.label}</span>
          </div>
          <div className="text-xs text-white/60 mt-0.5">
            <Calendar size={10} className="inline mr-1 -mt-0.5" weight="duotone" />
            {item.start_date} → {item.end_date} · <span className="font-mono">{item.days}j</span>
          </div>
          <div className="text-[10px] text-white/40 mt-0.5">
            Par <b>{item.user_name}</b> · {(item.created_at || "").slice(0, 10)}
          </div>
          {item.reason && <div className="text-xs text-white/70 mt-1.5 italic">« {item.reason} »</div>}
          {item.decision_note && (
            <div className="text-xs text-white/70 mt-1.5 rounded-md bg-white/5 px-2 py-1.5">
              <span className="text-[9px] uppercase tracking-widest text-white/40">Réponse {item.decided_by_name}</span>
              <div>{item.decision_note}</div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {isAdmin && item.status === "pending" && (
            <Button size="sm" onClick={onDecide} className="bg-[#005CFF] hover:bg-[#0047CC] h-8 px-2.5" data-testid={`leave-decide-${item.id}`}>
              <CheckCircle size={12} weight="bold" className="mr-1" /> Décider
            </Button>
          )}
          {canCancel && (
            <button onClick={onCancel} className="text-white/30 hover:text-red-400 p-1.5" data-testid={`leave-cancel-${item.id}`}>
              <Trash size={12} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CreateLeaveDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ type: "paid", start_date: "", end_date: "", reason: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!open) setForm({ type: "paid", start_date: "", end_date: "", reason: "" }); }, [open]);

  const submit = async () => {
    if (!form.start_date || !form.end_date) { toast.error("Dates requises"); return; }
    if (form.end_date < form.start_date) { toast.error("La date de fin doit être postérieure à la date de début"); return; }
    setSaving(true);
    try {
      await api.post("/leave-requests", form);
      onOpenChange(false);
      setTimeout(() => toast.success("Demande envoyée"), 50);
      onCreated?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const days = (() => {
    if (!form.start_date || !form.end_date || form.end_date < form.start_date) return 0;
    const s = new Date(form.start_date), e = new Date(form.end_date);
    return Math.max(1, Math.round((e - s) / 86400000) + 1);
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[#111] border-white/10 text-white rounded-2xl" data-testid="leave-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Nouvelle demande</DialogTitle>
          <DialogDescription className="text-[11px] uppercase tracking-widest text-white/50">
            Le gérant sera notifié par push et email
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-white/60 text-[11px] uppercase tracking-widest">Type</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger className="bg-white/5 border-white/10" data-testid="leave-type"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#111] border-white/10 text-white">
                {Object.entries(TYPE_META).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-white/60 text-[11px] uppercase tracking-widest">Du</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="bg-white/5 border-white/10 [color-scheme:dark]" data-testid="leave-start" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/60 text-[11px] uppercase tracking-widest">Au</Label>
              <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="bg-white/5 border-white/10 [color-scheme:dark]" data-testid="leave-end" />
            </div>
          </div>
          {days > 0 && <div className="text-[10px] text-white/50 text-center">Soit <span className="font-mono text-white">{days} jour{days > 1 ? "s" : ""}</span></div>}
          <div className="space-y-1.5">
            <Label className="text-white/60 text-[11px] uppercase tracking-widest">Motif (optionnel)</Label>
            <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="bg-white/5 border-white/10 min-h-[70px]" placeholder="Ex: mariage, rendez-vous médical…" data-testid="leave-reason" />
          </div>
        </div>
        <div className="flex gap-2 mt-2">
          <Button variant="ghost" className="flex-1 border border-white/10" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} disabled={saving} className="flex-1 bg-[#005CFF] hover:bg-[#0047CC]" data-testid="leave-submit">
            {saving ? "…" : "Envoyer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DecideDialog({ req, onOpenChange, onDone }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (req) setNote(""); }, [req?.id]);
  if (!req) return null;

  const decide = async (status) => {
    setSaving(true);
    try {
      await api.patch(`/leave-requests/${req.id}`, { status, decision_note: note });
      onOpenChange(false);
      setTimeout(() => toast.success(status === "approved" ? "Demande acceptée" : "Demande refusée"), 50);
      onDone?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!req} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[#111] border-white/10 text-white rounded-2xl" data-testid="leave-decide-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-lg">Décision — {req.user_name}</DialogTitle>
          <DialogDescription className="text-[11px] uppercase tracking-widest text-white/50">
            {(TYPE_META[req.type] || {}).label} · {req.start_date} → {req.end_date} · {req.days}j
          </DialogDescription>
        </DialogHeader>
        {req.reason && <div className="text-xs text-white/70 italic rounded-md bg-white/5 p-2">« {req.reason} »</div>}
        <div className="space-y-1.5">
          <Label className="text-white/60 text-[11px] uppercase tracking-widest">Note pour l&apos;employé (optionnel)</Label>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} className="bg-white/5 border-white/10 min-h-[70px]" placeholder="Ex: OK — pense à débriefer avant de partir" data-testid="leave-decision-note" />
        </div>
        <div className="flex gap-2 mt-2">
          <Button onClick={() => decide("rejected")} disabled={saving} variant="ghost" className="flex-1 border border-red-500/40 text-red-300 hover:bg-red-500/10" data-testid="leave-reject-btn">
            <XCircle size={13} weight="bold" className="mr-1" /> Refuser
          </Button>
          <Button onClick={() => decide("approved")} disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-500" data-testid="leave-approve-btn">
            <CheckCircle size={13} weight="bold" className="mr-1" /> Accepter
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
